/**
 * Theme configuration for light and dark modes.
 * Defines semantic colour tokens and theme utilities.
 */

export type ThemeMode = 'light' | 'dark' | 'system'

export interface ThemeColours {
  background: string
  foreground: string
  card: string
  cardForeground: string
  primary: string
  primaryForeground: string
  secondary: string
  secondaryForeground: string
  muted: string
  mutedForeground: string
  accent: string
  accentForeground: string
  destructive: string
  destructiveForeground: string
  border: string
  ring: string
}

export const lightTheme: ThemeColours = {
  background: '#ffffff',
  foreground: '#0a0a0a',
  card: '#ffffff',
  cardForeground: '#0a0a0a',
  primary: '#7c3aed',
  primaryForeground: '#ffffff',
  secondary: '#f5f5f5',
  secondaryForeground: '#171717',
  muted: '#f5f5f5',
  mutedForeground: '#737373',
  accent: '#f5f3ff',
  accentForeground: '#4c1d95',
  destructive: '#ef4444',
  destructiveForeground: '#ffffff',
  border: '#e5e5e5',
  ring: '#7c3aed',
}

export const darkTheme: ThemeColours = {
  background: '#0a0a0a',
  foreground: '#fafafa',
  card: '#171717',
  cardForeground: '#fafafa',
  primary: '#8b5cf6',
  primaryForeground: '#ffffff',
  secondary: '#262626',
  secondaryForeground: '#fafafa',
  muted: '#262626',
  mutedForeground: '#a3a3a3',
  accent: '#2e1065',
  accentForeground: '#ede9fe',
  destructive: '#dc2626',
  destructiveForeground: '#fafafa',
  border: '#262626',
  ring: '#8b5cf6',
}

/**
 * Resolve theme mode considering system preference.
 */
export function resolveThemeMode(mode: ThemeMode, systemPrefersDark: boolean): 'light' | 'dark' {
  if (mode === 'system') return systemPrefersDark ? 'dark' : 'light'
  return mode
}

/**
 * Get the colour set for a resolved theme.
 */
export function getThemeColours(resolved: 'light' | 'dark'): ThemeColours {
  return resolved === 'dark' ? darkTheme : lightTheme
}

/**
 * Storage key for persisting theme preference.
 */
export const THEME_STORAGE_KEY = 'zero-theme-mode'

/**
 * Get the stored theme mode from localStorage, defaulting to 'system'.
 */
export function getStoredThemeMode(): ThemeMode {
  if (typeof window === 'undefined') return 'system'
  const stored = localStorage.getItem(THEME_STORAGE_KEY)
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  return 'system'
}

/**
 * Save theme mode to localStorage and apply to document.
 */
export function setThemeMode(mode: ThemeMode): void {
  localStorage.setItem(THEME_STORAGE_KEY, mode)
  const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  const resolved = resolveThemeMode(mode, systemPrefersDark)
  document.documentElement.classList.toggle('dark', resolved === 'dark')
}

/**
 * Initialize theme from stored preference or system preference.
 * Also sets up a listener for system preference changes when mode is 'system'.
 */
export function initializeTheme(): ThemeMode {
  const mode = getStoredThemeMode()
  setThemeMode(mode)

  // Listen for system preference changes so 'system' mode updates in real-time
  const mq = window.matchMedia('(prefers-color-scheme: dark)')
  mq.addEventListener('change', () => {
    const current = getStoredThemeMode()
    if (current === 'system') {
      setThemeMode('system')
    }
  })

  return mode
}
